import type { StreamProvider } from '@vgtv/api-client';
import type { Config } from '@schibsted-svp/web-player';

import type {
  AdnContext,
  PlayerConfigOptions,
  PlayerConfigPaywall,
} from '../types/global';
import { areAirplayAdsEnabled } from '../utils/airplayAds';

import { getAabConfigURL } from './aabConfig';
import { AutoplayManager } from './adn/ab/AutoplayManager';
import getAdnSettingsAb from './adn/ab/getAdnSettings';
import getUserId from './adn/getUserId';
import { withPPID } from './adn/ppid';
import { getConsentManager } from './adn/privacy';
import getAdnSettingsVgtv from './adn/vgtv/getAdnSettings';
import getPlayerToken, { TOKEN_API_HOSTNAME } from './paywall/getPlayerToken';
import { countryMap } from './countryMap';
import { defaultConfig, defaultOptions } from './defaults';
import { getExperimentOverrides } from './experimentOverride';
import { resolveExperiments } from './experiments';
import { getDnaUrl, getPluginUrl } from './helpers/staticUrls';
import * as SPiD from './paywall/spid';
import { createPulseDecorator } from './pulse';
import { resolveDnaStyleOption } from './vendorToDnaBrand';
import { getClientKeywords } from './adn/getClientKeywords';

export type SchibstedAccount = typeof SPiD & { env: 'PRE' | 'PRO' | 'PRO_NO' };

export default function getPlayerConfig(
  options: Partial<PlayerConfigOptions>,
  config: Config
): Config {
  options = {
    ...defaultOptions,
    ...options,
    plugins: {
      ...defaultOptions.plugins,
      ...options.plugins,
    },
    ads: {
      ...defaultOptions.ads,
      ...options.ads,
    },
  };

  const vendor = (config.vendor || defaultConfig.vendor) as StreamProvider;
  const dnaStyle = resolveDnaStyleOption(vendor, options.dnaStyle);

  const providerCountry = Object.keys(countryMap).find((k) =>
    countryMap[k].includes(vendor)
  );

  const dnaLinkId = `player-dna-${dnaStyle}`;
  if (dnaStyle && !document.getElementById(dnaLinkId)) {
    const link = document.createElement('link');
    link.id = dnaLinkId;
    link.rel = 'stylesheet';
    link.href = getDnaUrl(dnaStyle);
    document.head.appendChild(link);
  }

  const internalExperiments = Object.values(resolveExperiments(vendor)).map(
    (e) => Object.assign({}, e, { platform: vendor })
  );
  const externalExperiments = (options.experiments ?? [])?.map((e) =>
    Object.assign({}, e, { platform: vendor })
  );
  const experiments = internalExperiments.concat(externalExperiments);

  const experimentMap: Record<string, string> = {
    ...(process.env.NODE_ENV === 'staging' ? { vmap: 'enabled' } : {}),
    ...experiments.reduce(
      (aggr, { id, variant }) => ({ ...aggr, [id]: variant }),
      {} as Record<string, string>
    ),
    ...getExperimentOverrides(),
  };

  const settings: Config['settings'] = {
    ...defaultConfig.settings,
    ...config.settings,
    jw: { ...defaultConfig?.settings?.jw, ...config?.settings?.jw },
  };

  const paywallAccess: PlayerConfigPaywall = {
    ...defaultOptions.paywallAccess!,
    ...options.paywallAccess,
    message: {
      ...defaultOptions.paywallAccess?.message,
      ...options.paywallAccess?.message,
    },
  };

  if (config.skin?.name === 'video') {
    config.skin = defaultConfig.skin;
  } else {
    config.skin = {
      ...defaultConfig.skin,
      ...config.skin,
    };
  }

  const configOverrides: Partial<Config> & {
    disableCountdownPlugin?: boolean;
  } = {};

  if (TOKEN_API_HOSTNAME[vendor]) {
    configOverrides.tokenV2Url =
      TOKEN_API_HOSTNAME[vendor]!['pro'].concat('v2/');
  }

  const consentManager = getConsentManager(vendor);
  const adnContext: AdnContext = {
    consentManager,
    autoplayManager:
      providerCountry === 'se' ? new AutoplayManager() : undefined,
    identityHelper: SPiD,
    getClientKeywords,
  };

  if (providerCountry === 'no' || experimentMap.vmap === 'enabled') {
    settings.vmap = async () =>
      adnContext.adnPlugin?.getVmap({
        site: options.site!,
        experimentMap,
        consentManager,
      });
  }

  let plugins: Config['plugins'] = {};

  if (config.plugins) {
    plugins = { ...config.plugins };
  } else if (options.plugins?.mediaSession) {
    plugins[getPluginUrl('media-session')] = {
      name: 'MediaSessionPlugin',
      options: {
        titleOverride: options.ui?.titleOverride,
        hideTitle: options.ui?.hideTitle,
      },
    };
  }

  if (providerCountry === 'no' || vendor === 'ab') {
    plugins[getPluginUrl('adn')] = {
      name: 'AdnPlugin',
      options: {
        options,
        context: adnContext,
        providerCountry,
      },
    };
  }

  if (config.skin?.name === 'vgtv2') {
    let schibstedAccountEnv: 'PRE' | 'PRO' | 'PRO_NO';

    if (paywallAccess.usePre) {
      schibstedAccountEnv = 'PRE';
    } else {
      schibstedAccountEnv = providerCountry === 'no' ? 'PRO_NO' : 'PRO';
    }

    const recommended: Config['recommended'] =
      typeof config.recommended === 'boolean'
        ? {}
        : {
            ...defaultConfig.recommended,
            ...config.recommended,
          };

    const recommendedAutoplay = Boolean(
      recommended.autoplay || recommended.grid || recommended.next
    );

    plugins[getPluginUrl('skin-vgtv2')] = {
      name: 'VgtvSkinPlugin',
      options: {
        dnaStyle: options.dnaStyle,
        schibstedAccount: { ...SPiD, env: schibstedAccountEnv },
        context: options.context ?? 'embed',
        playlist: options.playlist,
        ui: options.ui,
        floating: options.floating,
        experiments,
        podcast: options.podcast,
        recommended: {
          autoplay: recommendedAutoplay,
        },
        utmParameters: paywallAccess.message?.utmParameters,
        getSubscriptionUrl: paywallAccess.getSubscriptionUrl,
        onLoginClick:
          paywallAccess.message?.onLoginClick ||
          (() => {
            if (paywallAccess.message?.onRedirect instanceof Function) {
              paywallAccess.message.onRedirect();
            }

            SPiD.login();
          }),
      },
    };

    SPiD.initIdentity(paywallAccess.getSpidIdentityInstance);
    SPiD.initMonetization(
      paywallAccess.getSpidMonetizationInstance,
      vendor,
      schibstedAccountEnv
    );

    Object.assign(settings.jw!, {
      controls: false,
      displaytitle: false,
      fullscreenOrientationLock: 'none',
      plugins: {
        [getPluginUrl('chromecast')]: {
          vendor,
          site: options.site,
          externalUid: getUserId(),
          consentManager,
          getUserAccesses: SPiD.getUserAccesses,
        },
      },
    });

    configOverrides.recommended = false;
    configOverrides.disableCountdownPlugin = true;
  }

  if (
    options.plugins?.topicSubscriber &&
    vendor === 'vgtv' &&
    paywallAccess.getSpidIdentityInstance
  ) {
    plugins[getPluginUrl('topic-subscriber')] = {
      name: 'TopicSubscriberPlugin',
      options: { getUserSettings: SPiD.getUserSettings, consentManager },
    };

    SPiD.initIdentity(paywallAccess.getSpidIdentityInstance);
  }

  if (options.plugins?.sifoTracking) {
    plugins[getPluginUrl('sifo-tracking')] = {
      name: 'SifoTracking',
      options: options.plugins.sifoTracking as unknown as Record<
        string,
        unknown
      >,
    };
  }

  if (options.plugins?.trackViewPercentage) {
    plugins[getPluginUrl('track-view-percentage')] = {
      name: 'TrackViewPercentage',
    };
  }

  if (options.plugins?.displayCoverTag) {
    plugins[getPluginUrl('display-cover-tag')] = {
      name: 'DisplayCoverTag',
      options: options.plugins.displayCoverTag as unknown as Record<
        string,
        unknown
      >,
    };
  }

  if (
    (vendor === 'ab' && config.skin?.name !== 'vgtv2') ||
    (config.skin?.name === 'vgtv2' &&
      // chromecast vs airplay
      (window.chrome || areAirplayAdsEnabled()))
  ) {
    // @ts-expect-error this is fine
    settings.jw.cast = undefined;
  }

  let floatingOverride: Config['floating'] | undefined;
  if (!config.floating && options.floating?.mode) {
    floatingOverride = {
      mode: options.floating.mode,
      dismissible: options.floating.dismissible ?? true,
    } as Config['floating'];
  }

  return {
    ...defaultConfig,
    ...config,
    sharing: false,
    settings,
    plugins,
    ttsSkin: false,
    ...(floatingOverride ? { floating: floatingOverride } : undefined),
    recommended: {
      ...defaultConfig?.recommended,
      ...(typeof config.recommended === 'boolean' ? {} : config.recommended),
    },
    pulse: {
      ...defaultConfig.pulse,
      ...config.pulse,
      tpaas: experimentMap.tpaas === 'enabled',
      decorator: createPulseDecorator(
        vendor,
        internalExperiments,
        adnContext,
        config.pulse?.decorator
      ),
    },
    ...(vendor === 'vgtv'
      ? {
          aab: 'auto',
          aabConfig: getAabConfigURL(),
        }
      : undefined),
    adn: withPPID(
      providerCountry === 'se' ? getAdnSettingsAb : getAdnSettingsVgtv
    ).bind(null, adnContext, options) as Config['adn'],
    ...(paywallAccess?.enabled
      ? {
          token: getPlayerToken(paywallAccess, vendor) as typeof config.token,
          identityReady: async () => {
            const snapshot = await SPiD.waitForInitialization();

            if (!snapshot.userId) {
              // NOT_AUTHENTICATED - Non-recoverable, the paywall UI takes over instead of retrying.
              throw 15103;
            }
          },
        }
      : {
          token: config.token,
        }),

    ...configOverrides,
  };
}
